
// helperCatalogs.js
import { concatDynamicLevelsHelper } from './helperConcat';

/** Texto visible de una fila de la tabla catalogs: description, y name solo si description viene vacía. */
export const catalogTableLabel = (row) => {
  if (!row || typeof row !== 'object') return '';
  const description = String(row.description || '').trim();
  if (description) return description;
  return String(row.name || '').trim();
};

export const getObjectByDescriptionHelper = (description, arraySearch) => {
  if (!description || !Array.isArray(arraySearch)) return undefined;

  const target = description.toString().trim().toLowerCase();

  return arraySearch.find(item => 
    item?.description?.toString().trim().toLowerCase() === target
  );
};


export const getObjectByDescriptionNoAccentsHelper = (description, arraySearch) => {
  if (!description || !Array.isArray(arraySearch)) return undefined;

  // Función interna para pasar a minúsculas, quitar espacios y eliminar tildes
  const cleanString = (str) => 
    str.toString()
       .trim()
       .toLowerCase()
       .normalize("NFD") // Separa las letras de sus tildes
       .replace(/[\u0300-\u036f]/g, ""); // Borra los caracteres de las tildes

  const target = cleanString(description);

  return arraySearch.find(item => {
    if (!item?.description) return false;
    return cleanString(item.description) === target;
  });
};

export const getObjectByIdHelper = (id, arraySearch) => {
  // Validación temprana: si no viene un ID válido o no es un array, aborta
  if ((id === null || id === undefined) || !Array.isArray(arraySearch)) return undefined;

  const rawId = (typeof id === 'object') ? id.id : id;
  if (rawId === null || rawId === undefined) return undefined;

  // Forzamos ambos lados a String para garantizar coincidencia sin importar el tipo
  const targetId = rawId.toString();

  return arraySearch.find(item => item?.id?.toString() === targetId);
};

export const hydrateCatalogSelectFields = (handleChange, mappings = []) => {
  if (typeof handleChange !== 'function' || !Array.isArray(mappings)) {
    return;
  }

  mappings.forEach(({ id, list, field }) => {
    if (id === undefined || id === null || id === '' || !field) {
      return;
    }
    if (!Array.isArray(list) || list.length === 0) {
      return;
    }

    const objectFound = getObjectByIdHelper(id, list);
    if (objectFound) {
      handleChange(objectFound, field);
    }
  });
};


/**
 * Busca y retorna los objetos de un catálogo basados en un array de descripciones.
 * @param {Array<string>} selectedTexts - Array de textos a buscar.
 * @param {Array<Object>} catalogList - Catálogo donde se realizará la búsqueda.
 * @returns {Array<Object>} Lista de objetos encontrados.
 */
export const getSelectedCatalogsByDescriptionHelper = (selectedTexts, catalogList) => {
  if (!Array.isArray(selectedTexts) || !Array.isArray(catalogList)) return [];
  
  return selectedTexts
    .map(text => getObjectByDescriptionHelper(text, catalogList))
    .filter(Boolean);
};


/**
 * Busca y retorna los objetos de un catálogo basados en un array de problemas seleccionados (por ID).
 * @param {Array<Object>} catalogProblemsSelected - Array de objetos con la propiedad catalogproblem_id.
 * @param {Array<Object>} listProblems - Catálogo maestro de problemas donde se realizará la búsqueda.
 * @returns {Array<Object>} Lista de objetos de problemas encontrados.
 */
export const getSelectedProblemsOriginalCatalogsHelper = (catalogProblemsSelected, listProblems) => {
  if (!Array.isArray(catalogProblemsSelected) || !Array.isArray(listProblems)) return [];

  return catalogProblemsSelected
    .map(problem => getObjectByIdHelper(problem?.catalogproblem_id, listProblems))
    .filter(Boolean); // Remueve de inmediato los valores undefined si no hubo coincidencia
};


/**
 * Convierte un string separado por un delimitador en un array de textos limpios.
 * @param {string} text - El string a convertir.
 * @param {string} [separator=";"] - El carácter de separación.
 * @returns {Array<string>} Array de strings limpios.
 */
export const stringToListHelper = (text, separator = ";") => {
  if (!text || typeof text !== "string") return [];
  
  return text
    .split(separator)
    .map(item => item.trim())
    .filter(Boolean); // Remueve elementos vacíos (ej. si el string termina en ";")
};

/**
 * Convierte una lista de objetos o strings en una cadena de texto separada por un delimitador.
 * @param {Array} list - Array de elementos a unir.
 * @param {string} [separator=";"] - El carácter de separación.
 * @param {string} [property="description"] - Propiedad del objeto a extraer (ignorado si la lista es de strings puros).
 * @returns {string} Cadena de texto unificada.
 */
export const listToStringHelper = (list, separator = ";", property = "description") => {
  if (!Array.isArray(list) || list.length === 0) return "";

  return list
    .map(item => {
      if (!item) return "";
      // Si el elemento ya es un string o número, lo usa directamente; si es objeto, busca la propiedad
      return typeof item === "object" ? item[property] : item;
    })
    .map(str => str.toString().trim())
    .filter(Boolean) // Evita puntos y comas seguidos si había elementos vacíos
    .join(separator);
};


/**
 * Busca un objeto en un arreglo mediante su código, eliminando espacios y garantizando compatibilidad de tipos.
 * @param {string|number} code - El código a buscar.
 * @param {Array<Object>} arraySearch - El catálogo donde se realizará la búsqueda.
 * @returns {Object|undefined} El objeto encontrado o undefined si no hay coincidencia.
 */
export const getObjectByCodeHelper = (code, arraySearch) => {
  if ((code === null || code === undefined) || !Array.isArray(arraySearch)) return undefined;

  // Pasamos a String y limpiamos espacios fantasmas (¡Crucial para el bug de partes[i]!)
  const targetCode = code.toString().trim();

  return arraySearch.find(item => item?.code?.toString().trim() === targetCode);
};

export const formatCatalogCiiuLastLevelLabel = (levelsValue, catalogCiius = []) => {
  if (levelsValue == null || levelsValue === '') {
    return '';
  }

  const parts = String(levelsValue)
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean);
  const lastCode = parts[parts.length - 1];
  if (!lastCode) {
    return '';
  }

  const found = getObjectByCodeHelper(lastCode, catalogCiius);
  const name = found?.name || found?.description || '';
  return name ? `${lastCode} - ${name}` : lastCode;
};

/**
 * Reescribe catalog*_levelN (objetos de combo) desde el string de códigos
 * sin pasar por handleChangeCatalog*, que borra los niveles hijos.
 */
export const hydrateMultilevelSelectFields = ({
  values,
  catalogList,
  prefix,
  idField,
  levelsField,
  maxLevels
}) => {
  if (!values || !prefix || !Array.isArray(catalogList) || catalogList.length === 0) {
    return {};
  }

  let partes = typeof values[levelsField] === 'string'
    ? stringToListHelper(values[levelsField])
    : [];
  if (partes.length === 0) {
    partes = stringToListHelper(concatDynamicLevelsHelper(values, prefix, maxLevels));
  }
  if (partes.length === 0) {
    return {};
  }

  const patch = {};
  let last = null;
  partes.forEach((code, index) => {
    const obj = getObjectByCodeHelper(code, catalogList);
    if (obj) {
      patch[`${prefix}_level${index + 1}`] = obj;
      last = obj;
    }
  });
  if (!last && values[idField] != null && values[idField] !== '') {
    const byId = getObjectByIdHelper(values[idField], catalogList);
    const level = Number(byId?.level);
    if (byId && level >= 1 && level <= maxLevels) {
      return {
        [`${prefix}_level${level}`]: byId,
        [idField]: byId.id,
        [levelsField]: partes.join(';') || byId.code
      };
    }
    return {};
  }
  if (!last) {
    return {};
  }
  patch[idField] = last.id;
  patch[levelsField] = partes.join(';');
  return patch;
};
